/**
 * =============================================================================
 * FALCON - Module Deneigement des Parkings Avion
 * =============================================================================
 *
 * @file        services/syncService.ts
 * @description Service de synchronisation pour le mode offline
 *              Gere la file d'attente des actions et la synchronisation
 *              avec le serveur quand la connexion est retablie
 *
 * @version     2.4
 * @author      Equipe FALCON
 * =============================================================================
 */

import apiClient from './api';
import {
  getPendingActions,
  markActionSynced,
  markActionFailed,
  queuePendingAction,
  getPendingActionCount,
  cacheIntervention,
  cacheInterventions,
  cacheZones,
  updateCachedZoneStatut,
  getCachedIntervention,
  getCachedZonesForIntervention,
  clearSyncedActions,
  type PendingAction,
  type PendingActionType,
} from './offlineDb';
import type { Deneignement, DeneignementZonesResponse } from '@/types';

// =============================================================================
// TYPES
// =============================================================================

export interface SyncResult {
  success: boolean;
  synced: number;
  failed: number;
  errors: Array<{ actionId: number; error: string }>;
}

export interface OfflineActionPayload {
  deneignementId: number;
  zoneId?: number;
  timestamp: string;
  gpsLatitude?: number;
  gpsLongitude?: number;
  commentaire?: string;
  [key: string]: unknown;
}

// =============================================================================
// SYNC SERVICE CLASS
// =============================================================================

class SyncService {
  private syncInProgress = false;
  private syncListeners: Set<(result: SyncResult) => void> = new Set();

  // ---------------------------------------------------------------------------
  // CONNECTION STATE
  // ---------------------------------------------------------------------------

  /**
   * Check if the device is currently online
   */
  isOnline(): boolean {
    return navigator.onLine;
  }

  /**
   * Get the number of pending (unsynced) actions
   */
  async getPendingCount(): Promise<number> {
    return getPendingActionCount();
  }

  // ---------------------------------------------------------------------------
  // QUEUE OPERATIONS
  // ---------------------------------------------------------------------------

  /**
   * Queue an action for later sync
   * @returns The ID of the queued action
   */
  async queueAction(
    type: PendingActionType,
    payload: OfflineActionPayload
  ): Promise<number> {
    const actionId = await queuePendingAction(type, payload);

    // If online, try to sync immediately
    if (this.isOnline() && !this.syncInProgress) {
      this.syncPendingActions().catch(console.error);
    }

    return actionId;
  }

  /**
   * Queue starting an intervention (offline-capable)
   */
  async queueInterventionStart(
    deneignementId: number,
    gpsCoords?: { latitude: number; longitude: number }
  ): Promise<number> {
    return this.queueAction('intervention_start', {
      deneignementId,
      timestamp: new Date().toISOString(),
      gpsLatitude: gpsCoords?.latitude,
      gpsLongitude: gpsCoords?.longitude,
    });
  }

  /**
   * Queue ending an intervention (offline-capable)
   */
  async queueInterventionEnd(
    deneignementId: number,
    options?: {
      travauxEffectues?: boolean;
      motifNonRealisation?: string;
      commentaireNonRealisation?: string;
      gpsCoords?: { latitude: number; longitude: number };
    }
  ): Promise<number> {
    return this.queueAction('intervention_end', {
      deneignementId,
      timestamp: new Date().toISOString(),
      gpsLatitude: options?.gpsCoords?.latitude,
      gpsLongitude: options?.gpsCoords?.longitude,
      travauxEffectues: options?.travauxEffectues,
      motifNonRealisation: options?.motifNonRealisation,
      commentaireNonRealisation: options?.commentaireNonRealisation,
    });
  }

  /**
   * Queue starting a zone (offline-capable)
   */
  async queueZoneStart(
    deneignementId: number,
    zoneId: number,
    gpsCoords?: { latitude: number; longitude: number }
  ): Promise<number> {
    // Update local cache immediately
    const timestamp = new Date().toISOString();
    await updateCachedZoneStatut(zoneId, 'en_cours', timestamp);

    return this.queueAction('zone_start', {
      deneignementId,
      zoneId,
      timestamp,
      gpsLatitude: gpsCoords?.latitude,
      gpsLongitude: gpsCoords?.longitude,
    });
  }

  /**
   * Queue ending a zone (offline-capable)
   */
  async queueZoneEnd(
    deneignementId: number,
    zoneId: number,
    commentaire?: string,
    gpsCoords?: { latitude: number; longitude: number }
  ): Promise<number> {
    // Update local cache immediately
    const timestamp = new Date().toISOString();
    await updateCachedZoneStatut(zoneId, 'termine', undefined, timestamp, commentaire);

    return this.queueAction('zone_end', {
      deneignementId,
      zoneId,
      timestamp,
      commentaire,
      gpsLatitude: gpsCoords?.latitude,
      gpsLongitude: gpsCoords?.longitude,
    });
  }

  // ---------------------------------------------------------------------------
  // SYNC OPERATIONS
  // ---------------------------------------------------------------------------

  /**
   * Sync all pending actions with the server
   */
  async syncPendingActions(): Promise<SyncResult> {
    if (this.syncInProgress) {
      return { success: false, synced: 0, failed: 0, errors: [] };
    }

    if (!this.isOnline()) {
      return { success: false, synced: 0, failed: 0, errors: [] };
    }

    this.syncInProgress = true;
    const result: SyncResult = {
      success: true,
      synced: 0,
      failed: 0,
      errors: [],
    };

    try {
      const pendingActions = await getPendingActions();

      // Sort by timestamp to maintain order
      pendingActions.sort((a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );

      for (const action of pendingActions) {
        try {
          await this.syncSingleAction(action);
          await markActionSynced(action.id!);
          result.synced++;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          await markActionFailed(action.id!, errorMessage);
          result.failed++;
          result.errors.push({ actionId: action.id!, error: errorMessage });

          // If action has failed too many times, skip it
          if (action.retryCount >= 5) {
            console.warn(`Action ${action.id} failed too many times, skipping`);
          }
        }
      }

      // Clean up synced actions
      await clearSyncedActions();

      result.success = result.failed === 0;
    } catch (error) {
      console.error('Sync failed:', error);
      result.success = false;
    } finally {
      this.syncInProgress = false;
      this.notifyListeners(result);
    }

    return result;
  }

  /**
   * Sync a single pending action
   */
  private async syncSingleAction(action: PendingAction): Promise<void> {
    const payload = action.payload as OfflineActionPayload;

    switch (action.type) {
      case 'intervention_start':
        await apiClient.patch(`/deneignements/${payload.deneignementId}/demarrer`, {
          gps_latitude: payload.gpsLatitude,
          gps_longitude: payload.gpsLongitude,
          timestamp: payload.timestamp,
        });
        break;

      case 'intervention_end':
        await apiClient.patch(`/deneignements/${payload.deneignementId}/terminer`, {
          travaux_effectues: payload.travauxEffectues,
          motif_non_realisation: payload.motifNonRealisation,
          commentaire_non_realisation: payload.commentaireNonRealisation,
          gps_latitude: payload.gpsLatitude,
          gps_longitude: payload.gpsLongitude,
          timestamp: payload.timestamp,
        });
        break;

      case 'zone_start':
        await apiClient.post(
          `/deneignements/${payload.deneignementId}/zones/${payload.zoneId}/demarrer`,
          {
            gps_latitude: payload.gpsLatitude,
            gps_longitude: payload.gpsLongitude,
            timestamp: payload.timestamp,
          }
        );
        break;

      case 'zone_end':
        await apiClient.post(
          `/deneignements/${payload.deneignementId}/zones/${payload.zoneId}/terminer`,
          {
            commentaire: payload.commentaire,
            gps_latitude: payload.gpsLatitude,
            gps_longitude: payload.gpsLongitude,
            timestamp: payload.timestamp,
          }
        );
        break;

      case 'photo_upload':
        // Photo uploads need special handling - skipped for now
        console.warn('Photo upload sync not yet implemented');
        break;

      case 'incident_report':
        // Incident reports need special handling
        await apiClient.post(`/deneignements/${payload.deneignementId}/incidents`, {
          ...payload,
          timestamp: payload.timestamp,
        });
        break;

      default:
        console.warn(`Unknown action type: ${action.type}`);
    }
  }

  // ---------------------------------------------------------------------------
  // DATA CACHING
  // ---------------------------------------------------------------------------

  /**
   * Cache intervention data for offline use
   */
  async cacheInterventionData(intervention: Deneignement): Promise<void> {
    await cacheIntervention(intervention);
  }

  /**
   * Cache multiple interventions
   */
  async cacheInterventionsData(interventions: Deneignement[]): Promise<void> {
    await cacheInterventions(interventions);
  }

  /**
   * Cache zones for an intervention
   */
  async cacheZonesData(deneignementId: number, zonesResponse: DeneignementZonesResponse): Promise<void> {
    await cacheZones(zonesResponse.zones, deneignementId);
  }

  /**
   * Get cached intervention (for offline use)
   */
  async getCachedIntervention(id: number) {
    return getCachedIntervention(id);
  }

  /**
   * Get cached zones for an intervention (for offline use)
   */
  async getCachedZones(deneignementId: number) {
    return getCachedZonesForIntervention(deneignementId);
  }

  // ---------------------------------------------------------------------------
  // LISTENERS
  // ---------------------------------------------------------------------------

  /**
   * Add a listener for sync completion
   */
  addSyncListener(callback: (result: SyncResult) => void): () => void {
    this.syncListeners.add(callback);
    return () => this.syncListeners.delete(callback);
  }

  private notifyListeners(result: SyncResult): void {
    this.syncListeners.forEach((listener) => {
      try {
        listener(result);
      } catch (error) {
        console.error('Sync listener error:', error);
      }
    });
  }

  // ---------------------------------------------------------------------------
  // BATCH SYNC (for backend mobile endpoint)
  // ---------------------------------------------------------------------------

  /**
   * Try to sync all actions in a single batch request
   * This is more efficient when there are many pending actions
   */
  async syncBatch(): Promise<SyncResult> {
    if (!this.isOnline()) {
      return { success: false, synced: 0, failed: 0, errors: [] };
    }

    const pendingActions = await getPendingActions();
    if (pendingActions.length === 0) {
      return { success: true, synced: 0, failed: 0, errors: [] };
    }

    try {
      // Try batch sync endpoint
      const response = await apiClient.post('/mobile/sync', {
        actions: pendingActions.map((action) => ({
          type: action.type,
          payload: action.payload,
          timestamp: action.timestamp,
        })),
      });

      const result: SyncResult = {
        success: true,
        synced: response.data.synced || pendingActions.length,
        failed: response.data.failed || 0,
        errors: response.data.errors || [],
      };

      // Mark all as synced
      for (const action of pendingActions) {
        await markActionSynced(action.id!);
      }

      await clearSyncedActions();
      this.notifyListeners(result);

      return result;
    } catch (error) {
      // If batch sync fails, fall back to individual sync
      console.warn('Batch sync failed, falling back to individual sync');
      return this.syncPendingActions();
    }
  }
}

// =============================================================================
// SINGLETON EXPORT
// =============================================================================

export const syncService = new SyncService();
export default syncService;
